import classNames from "classnames";
import React, { FC, useEffect, useMemo, useState } from "react";
import {
  LuDownload,
  LuEye,
  LuFolderInput,
  LuRotateCcw,
  LuSearch,
  LuTrash2,
  LuX,
} from "react-icons/lu";
import { Link, Navigate, NavLink, Route, Routes } from "react-router-dom";
import { useDebounce } from "usehooks-ts";

import { LottieLoading } from "../../components/graphics/LottieLoading";
import { ListPager } from "../../components/pagination/ListPager";
import { getLinkedFirm } from "../../sessionStorage/sessionStorage";
import {
  downloadZip,
  useDeleteFilesForeverMutation,
  useDeleteFilesMutation,
  useGetClientFilesQuery,
  useGetFirmFileClientsQuery,
  useGetFirmFilesQuery,
  useGetFirmFilesSummaryQuery,
  useMarkFilesSeenMutation,
  useMoveFilesMutation,
  useRestoreFilesMutation,
} from "../../slices/DocumentsSlice";
import { BillingMenuItem, ClientAvatar } from "../billing/ui/BillingUi";
import { DeleteFilesModal } from "./FileDialogs";
import { FileItem, FirmFilesQuery, FirmTab } from "./files.types";
import { refKey, toRef } from "./files.utils";
import { clientLink, FileTable } from "./FileTable";
import { FolderPickerModal } from "./FolderPicker";
import {
  ErrorText,
  errorText,
  INPUT,
  Pill,
  SECONDARY_BUTTON,
  SELECT,
} from "./ui/FilesUi";
import { useFileOpener } from "./useFileOpener";

const PAGE = 50;
// All clients tab: the list comes whole and is paged here.
const CLIENT_PAGE = 100;

/** Left menu "Documents": files across all clients (V49 look). */
export const DocumentsView: FC = () => {
  const orgId = getLinkedFirm()?.orgId || "";
  const [mine, setMine] = useState(true);
  const { data: summary } = useGetFirmFilesSummaryQuery(
    { orgId, mine },
    { skip: !orgId },
  );
  const mineOn = mine && !!summary?.mineAvailable;

  useEffect(() => {
    document.title = "Documents | ProCharted";
  }, []);

  const tabs: { to: string; label: string; count?: number }[] = [
    // Owner, Roadmap 1838: All files (newest first) / All clients / From
    // clients / Recycle bin; "Recent" is gone (All files is newest first).
    { to: "", label: "All files" },
    { to: "clients", label: "All clients" },
    {
      to: "from-clients",
      label: "From clients",
      count: summary?.newFromClients,
    },
    { to: "bin", label: "Recycle bin" },
  ];

  return (
    <div
      className={
        "-mx-4 -mt-4 min-h-full bg-[#F5F6FA] font-Manrope text-[#1F2433]"
      }>
      <div className={"border-b border-[#ECEEF2] bg-white px-6 pt-5"}>
        <div className={"mb-3 flex flex-wrap items-center gap-3"}>
          <h1 className={"text-[22px] font-extrabold tracking-tight"}>
            Documents
          </h1>
          <span className={"flex-1"} />
          {summary?.mineAvailable && (
            <label
              className={
                "flex cursor-pointer items-center gap-2 text-[13px] font-semibold text-[#4A5168]"
              }>
              <input
                id={"documents-mine"}
                type={"checkbox"}
                className={"checkbox checkbox-primary checkbox-sm"}
                checked={mine}
                onChange={(event) => setMine(event.target.checked)}
              />
              Only my clients
            </label>
          )}
        </div>
        <nav className={"flex gap-6"} aria-label={"Documents tabs"}>
          {tabs.map((tab) => (
            <NavLink
              key={tab.label}
              end={tab.to === ""}
              to={tab.to === "" ? "/documents" : `/documents/${tab.to}`}
              className={({ isActive }) =>
                classNames(
                  "relative flex items-center gap-1.5 pb-3 text-[13.5px] font-bold",
                  isActive
                    ? "text-[#4B32D6] after:absolute after:inset-x-0 after:-bottom-px after:h-0.5 after:rounded after:bg-[#6B4EFF]"
                    : "text-[#667085] hover:text-[#1F2433]",
                )
              }>
              {tab.label}
              {!!tab.count && (
                <span
                  className={
                    "rounded-full bg-[#6B4EFF] px-1.5 text-[11px] text-white"
                  }>
                  {tab.count}
                </span>
              )}
            </NavLink>
          ))}
        </nav>
      </div>
      <div className={"p-6"}>
        <Routes>
          <Route
            index={true}
            element={
              <FirmFilesTab orgId={orgId} tab={"recent"} mine={mineOn} />
            }
          />
          <Route
            path={"from-clients"}
            element={
              <FirmFilesTab orgId={orgId} tab={"from-clients"} mine={mineOn} />
            }
          />
          <Route
            path={"recent"}
            element={<Navigate to={"/documents"} replace={true} />}
          />
          <Route
            path={"clients"}
            element={<FirmClientsTab orgId={orgId} mine={mineOn} />}
          />
          <Route
            path={"bin"}
            element={
              <FirmFilesTab
                orgId={orgId}
                tab={"bin"}
                mine={mineOn}
                canDeleteForever={!!summary?.canDeleteForever}
              />
            }
          />
          <Route
            path={"*"}
            element={<Navigate to={"/documents"} replace={true} />}
          />
        </Routes>
      </div>
    </div>
  );
};

const INTRO: Record<FirmTab, string> = {
  "from-clients":
    "Files clients sent through the portal. “New” = nobody on your team has opened it yet.",
  recent: "Every file added to a client or work item, newest first.",
  bin: "Deleted files stay here for 90 days, then they are erased for good.",
};

const ANY_TIME_DAYS = 3650;

/** All files / From clients / Recycle bin. */
const FirmFilesTab: FC<{
  orgId: string;
  tab: FirmTab;
  mine: boolean;
  canDeleteForever?: boolean;
}> = ({ orgId, tab, mine, canDeleteForever }) => {
  const [search, setSearch] = useState("");
  const debounced = useDebounce(search, 300);
  const [status, setStatus] = useState<FirmFilesQuery["status"]>("open");
  const [type, setType] = useState<FirmFilesQuery["type"]>("");
  // 3650 = any time (the server's longest range).
  const [days, setDays] = useState(ANY_TIME_DAYS);
  const [page, setPage] = useState(1);
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [fileTo, setFileTo] = useState<FileItem[] | null>(null);
  const [confirm, setConfirm] = useState<{
    files: FileItem[];
    forever: boolean;
  } | null>(null);
  const [error, setError] = useState("");
  const [notice, setNotice] = useState("");

  const query: FirmFilesQuery = {
    orgId,
    tab,
    mine,
    search: debounced || undefined,
    status: tab === "from-clients" ? status : undefined,
    type: type || undefined,
    days: tab === "recent" ? days : undefined,
    skip: (page - 1) * PAGE,
    take: PAGE,
  };
  const {
    data,
    isLoading,
    isFetching,
    isError,
    error: loadError,
    refetch,
  } = useGetFirmFilesQuery(query, {
    skip: !orgId,
    refetchOnMountOrArgChange: true,
  });
  const [markSeen] = useMarkFilesSeenMutation();
  const [restore, { isLoading: restoring }] = useRestoreFilesMutation();
  const [deleteFiles, { isLoading: deleting }] = useDeleteFilesMutation();
  const [deleteForever, { isLoading: erasing }] =
    useDeleteFilesForeverMutation();
  const { openFile, downloadFile, preview } = useFileOpener(setError);

  useEffect(
    () => setSelected(new Set()),
    [tab, debounced, status, type, days, mine],
  );
  useEffect(() => setPage(1), [tab, debounced, status, type, days, mine]);
  // After deleting/filing the last files of the last page, step back a page.
  useEffect(() => {
    const last = Math.max(1, Math.ceil((data?.total ?? 0) / PAGE));
    if (data && page > last) setPage(last);
  }, [data, page]);
  useEffect(() => {
    if (!notice) return;
    const timer = setTimeout(() => setNotice(""), 4000);
    return () => clearTimeout(timer);
  }, [notice]);

  const rows = data?.rows || [];
  const selectedFiles = rows.filter((f) => selected.has(refKey(toRef(f))));
  const oneClient =
    selectedFiles.length > 0 &&
    selectedFiles.every(
      (f) => f.customerId && f.customerId === selectedFiles[0].customerId,
    );

  const run = async (action: () => Promise<unknown>, done: string) => {
    setError("");
    try {
      await action();
      setNotice(done);
      setSelected(new Set());
      setConfirm(null);
    } catch (e) {
      setError(errorText(e));
    }
  };

  const zip = (files: FileItem[]) =>
    run(() => downloadZip(files.map(toRef)), "Download started.").catch(
      () => undefined,
    );

  const menu = (file: FileItem): BillingMenuItem[] => {
    if (tab === "bin") {
      const items: BillingMenuItem[] = [
        {
          label: "Restore",
          icon: <LuRotateCcw size={15} />,
          onClick: () =>
            run(() => restore([toRef(file)]).unwrap(), "File restored."),
        },
      ];
      if (canDeleteForever) {
        items.push({
          label: "Erase now",
          icon: <LuTrash2 size={15} />,
          onClick: () => setConfirm({ files: [file], forever: true }),
        });
      }
      return items;
    }
    const items: BillingMenuItem[] = [
      {
        label: "Preview",
        icon: <LuEye size={15} />,
        onClick: () => openFile(file),
      },
      {
        label: "Download",
        icon: <LuDownload size={15} />,
        onClick: () => downloadFile(file),
      },
    ];
    if (file.customerId && (file.kind === "client" || file.workId)) {
      items.push({
        label: "File to…",
        icon: <LuFolderInput size={15} />,
        onClick: () => setFileTo([file]),
      });
    }
    return [
      ...items,
      {
        label: "Delete",
        icon: <LuTrash2 size={15} />,
        danger: true,
        onClick: () => setConfirm({ files: [file], forever: false }),
      },
    ];
  };

  return (
    <div>
      <p className={"mb-3 text-[13px] text-[#667085]"}>{INTRO[tab]}</p>
      <div className={"mb-3 flex flex-wrap items-center gap-2"}>
        <div className={"relative w-[280px] max-w-full"}>
          <LuSearch
            size={15}
            className={
              "pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-[#9AA0B0]"
            }
          />
          <input
            id={`documents-search-${tab}`}
            className={classNames(INPUT, "pl-9")}
            placeholder={"Search file, client or folder"}
            value={search}
            onChange={(event) => setSearch(event.target.value)}
          />
        </div>
        {tab === "from-clients" && (
          <select
            id={"documents-status"}
            aria-label={"Show"}
            className={SELECT}
            value={status}
            onChange={(e) =>
              setStatus(e.target.value as FirmFilesQuery["status"])
            }>
            <option value={"open"}>Show: new or not filed</option>
            <option value={"new"}>Show: new only</option>
            <option value={"unfiled"}>Show: not filed</option>
            <option value={"all"}>Show: everything</option>
          </select>
        )}
        {tab === "recent" && (
          <select
            id={"documents-days"}
            aria-label={"Added"}
            className={SELECT}
            value={days}
            onChange={(e) => setDays(Number(e.target.value))}>
            <option value={ANY_TIME_DAYS}>Added: any time</option>
            <option value={7}>Added: last 7 days</option>
            <option value={30}>Added: last 30 days</option>
            <option value={90}>Added: last 90 days</option>
            <option value={365}>Added: last 12 months</option>
          </select>
        )}
        <select
          id={`documents-type-${tab}`}
          aria-label={"Type"}
          className={SELECT}
          value={type}
          onChange={(e) => setType(e.target.value as FirmFilesQuery["type"])}>
          <option value={""}>Type: all</option>
          <option value={"pdf"}>PDF</option>
          <option value={"word"}>Word / text</option>
          <option value={"excel"}>Excel / CSV</option>
          <option value={"image"}>Photos</option>
          <option value={"other"}>Other</option>
        </select>
        {isFetching && !isLoading && (
          <span className={"text-xs text-[#9AA0B0]"}>Updating…</span>
        )}
      </div>

      <div
        className={
          "rounded-2xl border border-[#E6E9EF] bg-white px-3.5 py-1.5"
        }>
        {isLoading ? (
          <LottieLoading />
        ) : isError ? (
          <div className={"py-10 text-center text-[13px] text-[#4A5168]"}>
            {errorText(loadError, "The files couldn't be loaded.")}{" "}
            <button
              type={"button"}
              className={"font-bold text-[#5A3EEB] underline"}
              onClick={() => refetch()}>
              Try again
            </button>
          </div>
        ) : (
          <FileTable
            files={rows}
            columns={{
              client: true,
              location: true,
              addedBy: tab !== "bin",
              added: tab !== "bin",
              deleted: tab === "bin",
              size: tab !== "from-clients",
              status: tab === "from-clients",
            }}
            selected={selected}
            onSelect={setSelected}
            onOpen={(file) => (tab === "bin" ? undefined : openFile(file))}
            menu={menu}
            extra={(file) =>
              tab === "from-clients" && !file.folderId && file.customerId ? (
                <button
                  type={"button"}
                  className={classNames(
                    SECONDARY_BUTTON,
                    "h-[30px] whitespace-nowrap px-2.5 text-xs",
                  )}
                  onClick={() => setFileTo([file])}>
                  File to…
                </button>
              ) : null
            }
            empty={
              <div className={"py-12 text-center text-[13px] text-[#667085]"}>
                {tab === "from-clients"
                  ? "Nothing waiting. New files from clients will show up here."
                  : tab === "bin"
                  ? "The recycle bin is empty."
                  : "No files match."}
              </div>
            }
          />
        )}
        {!!data?.total && (
          <ListPager
            page={page}
            pageSize={PAGE}
            total={data.total}
            disabled={isFetching}
            emptyLabel={"No files"}
            onChange={(next) => {
              setPage(next);
              setSelected(new Set());
            }}
          />
        )}
      </div>

      {selectedFiles.length > 0 && (
        <div
          className={
            "sticky bottom-3 mt-3 flex flex-wrap items-center gap-1.5 rounded-xl bg-[#1F2433] px-3.5 py-2 text-[13px] font-bold text-white shadow-lg"
          }>
          <span className={"mr-2"}>{selectedFiles.length} selected</span>
          <span className={"flex-1"} />
          {tab === "bin" ? (
            <>
              <DarkButton
                onClick={() =>
                  run(
                    () => restore(selectedFiles.map(toRef)).unwrap(),
                    `${selectedFiles.length} file(s) restored.`,
                  )
                }
                disabled={restoring}>
                Restore
              </DarkButton>
              {canDeleteForever && (
                <DarkButton
                  danger={true}
                  onClick={() =>
                    setConfirm({ files: selectedFiles, forever: true })
                  }>
                  Erase now
                </DarkButton>
              )}
            </>
          ) : (
            <>
              {tab === "from-clients" && (
                <DarkButton
                  onClick={() =>
                    run(
                      () => markSeen(selectedFiles.map(toRef)).unwrap(),
                      "Marked as seen.",
                    )
                  }>
                  Mark as seen
                </DarkButton>
              )}
              {oneClient && (
                <DarkButton onClick={() => setFileTo(selectedFiles)}>
                  File to…
                </DarkButton>
              )}
              <DarkButton onClick={() => zip(selectedFiles)}>
                Download (.zip)
              </DarkButton>
              <DarkButton
                danger={true}
                onClick={() =>
                  setConfirm({ files: selectedFiles, forever: false })
                }>
                Delete
              </DarkButton>
            </>
          )}
          <button
            type={"button"}
            aria-label={"Clear selection"}
            className={"ml-1 rounded p-1 hover:bg-white/10"}
            onClick={() => setSelected(new Set())}>
            <LuX size={15} />
          </button>
        </div>
      )}
      {notice && (
        <div
          role={"status"}
          className={
            "mt-3 rounded-lg bg-[#E3F6EE] px-3 py-2 text-[13px] font-semibold text-[#0E8A5F]"
          }>
          {notice}
        </div>
      )}
      <ErrorText>{error}</ErrorText>

      {preview}
      {fileTo && fileTo[0].customerId && (
        <FileToModal
          files={fileTo}
          customerId={fileTo[0].customerId}
          onClose={() => setFileTo(null)}
          onDone={(message) => {
            setFileTo(null);
            setSelected(new Set());
            setNotice(message);
          }}
        />
      )}
      {confirm && (
        <DeleteFilesModal
          title={
            confirm.forever
              ? `Erase ${
                  confirm.files.length === 1
                    ? "this file"
                    : `${confirm.files.length} files`
                } now?`
              : `Delete ${
                  confirm.files.length === 1
                    ? "this file"
                    : `${confirm.files.length} files`
                }?`
          }
          description={
            confirm.forever
              ? "This can’t be undone: the files are erased from ProCharted right away."
              : "They go to the recycle bin. You can restore them for 90 days; after that they are erased."
          }
          confirmLabel={confirm.forever ? "Erase now" : "Delete"}
          busy={deleting || erasing}
          error={error}
          onClose={() => setConfirm(null)}
          onConfirm={() =>
            confirm.forever
              ? run(
                  () => deleteForever(confirm.files.map(toRef)).unwrap(),
                  "Erased.",
                )
              : run(
                  () => deleteFiles(confirm.files.map(toRef)).unwrap(),
                  "Moved to the recycle bin.",
                )
          }
        />
      )}
    </div>
  );
};

/** "File to…" from the Documents page: loads that client's folders. */
const FileToModal: FC<{
  files: FileItem[];
  customerId: string;
  onClose: () => void;
  onDone: (message: string) => void;
}> = ({ files, customerId, onClose, onDone }) => {
  const { data, isLoading } = useGetClientFilesQuery(customerId);
  const [moveFiles, { isLoading: moving }] = useMoveFilesMutation();
  const [error, setError] = useState("");
  if (isLoading || !data) return null;
  return (
    <FolderPickerModal
      title={`File ${
        files.length === 1 ? `“${files[0].name}”` : `${files.length} files`
      }`}
      subtitle={data.name}
      customerId={customerId}
      folders={data.folders}
      initial={files.length === 1 ? files[0].folderId : null}
      confirmLabel={"File here"}
      busy={moving}
      error={error}
      onClose={onClose}
      onPick={async (folderId) => {
        setError("");
        try {
          await moveFiles({ items: files.map(toRef), folderId }).unwrap();
          const name = folderId
            ? data.folders.find((f) => f.id === folderId)?.path
            : "the top level";
          onDone(`Filed in ${data.name} › ${name}.`);
        } catch (e) {
          setError(errorText(e));
        }
      }}
    />
  );
};

/** "All clients": each client with its number of files and new uploads. */
const FirmClientsTab: FC<{ orgId: string; mine: boolean }> = ({
  orgId,
  mine,
}) => {
  const [search, setSearch] = useState("");
  const debounced = useDebounce(search, 300);
  const { data, isLoading } = useGetFirmFileClientsQuery(
    { orgId, mine, search: debounced || undefined },
    { skip: !orgId },
  );
  const [page, setPage] = useState(1);
  useEffect(() => setPage(1), [debounced, mine]);
  const rows = useMemo(
    () => (data || []).slice((page - 1) * CLIENT_PAGE, page * CLIENT_PAGE),
    [data, page],
  );

  return (
    <div>
      <p className={"mb-3 text-[13px] text-[#667085]"}>
        Open a client to see their folders. Clients with new uploads come first.
      </p>
      <div className={"relative mb-3 w-[280px] max-w-full"}>
        <LuSearch
          size={15}
          className={
            "pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-[#9AA0B0]"
          }
        />
        <input
          id={"documents-clients-search"}
          className={classNames(INPUT, "pl-9")}
          placeholder={"Search clients"}
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
      </div>
      <div
        className={
          "overflow-hidden rounded-2xl border border-[#E6E9EF] bg-white"
        }>
        {isLoading ? (
          <LottieLoading />
        ) : rows.length === 0 ? (
          <div className={"py-12 text-center text-[13px] text-[#667085]"}>
            No clients match.
          </div>
        ) : (
          <ul>
            {rows.map((row) => (
              <li
                key={row.customerId}
                className={"border-b border-[#EDEFF3] last:border-b-0"}>
                <Link
                  to={clientLink(row)}
                  className={
                    "flex items-center gap-3 px-4 py-3 hover:bg-[#FAFAFD]"
                  }>
                  <ClientAvatar
                    client={{
                      id: row.customerId,
                      name: row.name,
                      isOrganization: row.isOrganization,
                    }}
                    size={32}
                  />
                  <span className={"min-w-0 flex-1"}>
                    <span className={"block truncate text-[13.5px] font-bold"}>
                      {row.name}
                    </span>
                    <span className={"block text-xs text-[#667085]"}>
                      {row.fileCount} file{row.fileCount === 1 ? "" : "s"}
                      {row.lastAddedAt
                        ? ` · last added ${new Date(
                            row.lastAddedAt,
                          ).toLocaleDateString("en-US", {
                            month: "short",
                            day: "numeric",
                            year: "numeric",
                          })}`
                        : ""}
                    </span>
                  </span>
                  {row.newCount > 0 && (
                    <Pill tone={"new"}>{row.newCount} new</Pill>
                  )}
                  <span className={"text-[13px] font-semibold text-[#5A3EEB]"}>
                    Open files
                  </span>
                </Link>
              </li>
            ))}
          </ul>
        )}
        {!!data?.length && (
          <ListPager
            page={page}
            pageSize={CLIENT_PAGE}
            total={data.length}
            emptyLabel={"No clients"}
            onChange={setPage}
          />
        )}
      </div>
    </div>
  );
};

const DarkButton: FC<{
  onClick: () => void;
  danger?: boolean;
  disabled?: boolean;
  children: React.ReactNode;
}> = ({ onClick, danger, disabled, children }) => (
  <button
    type={"button"}
    disabled={disabled}
    onClick={onClick}
    className={classNames(
      "rounded-md px-2.5 py-1 font-semibold hover:bg-white/10 disabled:opacity-50",
      danger ? "text-[#FF9EA1]" : "text-white",
    )}>
    {children}
  </button>
);

export default DocumentsView;
